import { get, isObject, pick } from 'lodash-es'
import type { SearchApiFacets } from '~/api/search-api.types'
import type {
  DestinationCode,
  DestinationFacet,
  SearchState,
} from './search.types'

export const SEARCH_PAGE_TYPE = 'search' as const
export const SEARCH_PAGE_DEFAULT_PATH = '/search' as const

export const DESTINATIONS_MAP = shallowRef(
  new Map<DestinationCode, DestinationFacet>(),
)

export const DISTANCE_FILTERS = [
  'sea',
  'lake',
  'ski',
  'center',
  'publicTransport',
] as const

const SEARCH_URL_PARAMS = [
  'language',
  'currency',
  'season',
  'page',
  'sorting',
  'brand',
  'salesoffice',
  'q',
  'checkin',
  'checkout',
  'duration',
  'fuzzyness',
  'country',
  'region',
  'place',
  'code',
  'geo',
  'n',
  'w',
  's',
  'e',
  'zoom',
  'lat',
  'lon',
  'radius',
  'pax',
  'pets',
  'minpets',
  'house',
  'apartment',
  'villa',
  'chalet',
  'farmhouse',
  'detached',
  'stars',
  'rating',
  'reviews',
  'bedrooms',
  'bathrooms',
  'sea',
  'lake',
  'ski',
  'center',
  'public-transport',
  'wlan',
  'aircondition',
  'parking',
  'garage',
  'balcony-or-terrace',
  'dishwasher',
  'washingmachine',
  'tv',
  'sea_or_lake_view',
  'bbq',
  'boat',
  'cots',
  'hottub',
  'fireplace',
  'sauna',
  'wheelchair',
  'charging_station',
  'pool',
  'pool_private',
  'pool_indoor',
  'pool_children',
  'min-price',
  'maxPrice',
  'discount',
  'special_offer',
  'last_minute',
  'early_booker',
  'discount-20',
  'cheapcheap',
  'familyfriendly',
  'holiday_resort',
  'residence',
  'citytrips',
  'casa',
  'swiss_peak',
  'workation',
  'sustainable',
  'skiing',
  'hiking',
  'golfing',
  'cycling',
  'wellness',
  'tennis',
  'surfing',
  'sailing',
  'mountainbiking',
  'riding',
  'crosscountryskiing',
  'fishing',
  'fishing_certified',
  'studio',
  'lso',
  'bo',
  'rooms',
  'freeweekCategoryA',
  'freeweekCategoryB',
  'freeweekCategoryC',
] as const satisfies ReadonlyArray<keyof SearchURLParams>
const SEARCH_URL_PARAMS_SET = new Set<string>(SEARCH_URL_PARAMS)

export function isSearchURLParam(key: string): key is keyof SearchURLParams {
  return SEARCH_URL_PARAMS_SET.has(key)
}

export function getDestinationFacetByCode(
  code: DestinationCode,
): DestinationFacet | undefined {
  return DESTINATIONS_MAP.value.get(code)
}

export function addDestinationFacet(facet: DestinationFacet): DestinationFacet {
  DESTINATIONS_MAP.value.set(
    facet.code,
    Object.assign(getDestinationFacetByCode(facet.code) ?? {}, toRaw(facet)),
  )
  return facet
}

export function triggerDestinationFacetsRef() {
  triggerRef(DESTINATIONS_MAP)
}

export function pickSearchFilterConfs(
  confs: SearchFilterConf[],
  filters: Record<string, any>,
) {
  return confs.filter(({ filter, slug }) => {
    const filterValue = filters[filter]
    return filterValue && filterValue.toString() === slug.value
  })
}

export type FilterType = 'single' | 'multi' | 'range' | 'stepper'

export interface FilterOption<TValue = any> {
  value: TValue
  count?: number
}

export type FilterOptionValue = string | number | boolean

export type FilterConfOption =
  | { value: FilterOptionValue; slug: string }
  | FilterOptionValue

export type FilterConf = {
  key: string
  enabled: () => boolean
  searchStateKey: keyof SearchState
  searchApiKeys: (keyof SearchApiFacets)[]
  type: FilterType
  valueType: 'number' | 'string' | 'array'
  options: FilterConfOption[]
}

export function getFilterOptions(
  conf: FilterConf,
  facets: SearchApiFacets,
): FilterOption[] {
  switch (conf.type) {
    case 'single':
    case 'multi':
      return getSelectFilterOptions(conf, facets)
    case 'range':
      return getRangeFilterOptions(conf, facets)
    default:
      return getNoCountFilterOptions(conf)
  }
}

function getSelectFilterOptions(conf: FilterConf, facets: SearchApiFacets) {
  const facetsMap = new Map(
    Object.values(pick(facets, conf.searchApiKeys)).flatMap(Object.entries),
  )

  return conf.options.map((option) => {
    const [value, facetName] = isObject(option)
      ? [option.value, option.slug]
      : [option, String(option)]
    return {
      value: conf.valueType === 'number' ? (tryParseFloat(value) ?? 0) : value,
      count: facetsMap.get(facetName),
    }
  })
}

function getRangeFilterOptions(conf: FilterConf, facets: SearchApiFacets) {
  return conf.searchApiKeys
    .flatMap((key: string) => get(facets, key, []))
    .map(({ from, to, count }) => ({
      value: [tryParseFloat(from) ?? 0, tryParseFloat(to) ?? 0] as NumInterval,
      count: tryParseInt(count),
    }))
}

function getNoCountFilterOptions(conf: FilterConf) {
  return conf.options.map((option) => ({
    value: isObject(option) ? option.value : option,
  }))
}

const FILTER_CONFS: FilterConf[] = [
  {
    key: 'priceRange',
    enabled: () => true,
    searchStateKey: 'priceRange',
    searchApiKeys: ['price'],
    type: 'range',
    valueType: 'number',
    options: [],
  },
  {
    key: 'bedrooms',
    enabled: () => true,
    searchStateKey: 'bedrooms',
    searchApiKeys: ['types'],
    type: 'stepper',
    valueType: 'number',
    options: [BEDROOMS_MIN, BEDROOMS_MAX],
  },
  {
    key: 'bathrooms',
    enabled: () => true,
    searchStateKey: 'bathrooms',
    searchApiKeys: ['types'],
    type: 'stepper',
    valueType: 'number',
    options: [BATHROOMS_MIN, BATHROOMS_MAX],
  },
  {
    key: 'accommodationType',
    enabled: () => true,
    searchStateKey: 'accommodationType',
    searchApiKeys: ['types'],
    type: 'single',
    valueType: 'string',
    options: ['house', 'apartment'],
  },
  {
    key: 'propertyType',
    enabled: () => true,
    searchStateKey: 'propertyType',
    searchApiKeys: ['types', 'attributes'],
    type: 'multi',
    valueType: 'array',
    options: ['villa', 'chalet', 'farmhouse', 'studio', 'detached'],
  },
  {
    key: 'starRating',
    enabled: () => true,
    searchStateKey: 'starRating',
    searchApiKeys: ['stars'],
    type: 'single',
    valueType: 'number',
    options: [3, 4, 5],
  },
  {
    key: 'features',
    enabled: () => true,
    searchStateKey: 'features',
    searchApiKeys: ['attributes'],
    type: 'multi',
    valueType: 'array',
    options: [
      'pets',
      'wlan',
      'aircondition',
      'parking',
      'garage',
      'balcony-or-terrace',
      'dishwasher',
      'washingmachine',
      'tv',
      { value: 'sea_or_lake_view', slug: 'sea-or-lake-view' },
      'bbq',
      'boat',
      'cots',
      'hottub',
      'fireplace',
      'sauna',
      { value: 'charging_station', slug: 'charging-station' },
    ],
  },
  {
    key: 'sea',
    enabled: () => true,
    searchStateKey: 'sea',
    searchApiKeys: ['sea'],
    type: 'single',
    valueType: 'number',
    options: [100, 500, 1000, 5000],
  },
  {
    key: 'lake',
    enabled: () => true,
    searchStateKey: 'lake',
    searchApiKeys: ['lake'],
    type: 'single',
    valueType: 'number',
    options: [100, 500, 1000, 5000],
  },
  {
    key: 'ski',
    enabled: () => true,
    searchStateKey: 'ski',
    searchApiKeys: ['ski'],
    type: 'single',
    valueType: 'number',
    options: [100, 500, 1000, 5000],
  },
  {
    key: 'center',
    enabled: () => true,
    searchStateKey: 'center',
    searchApiKeys: ['center'],
    type: 'single',
    valueType: 'number',
    options: [100, 500, 1000, 5000],
  },
  {
    key: 'publicTransport',
    enabled: () => true,
    searchStateKey: 'publicTransport',
    searchApiKeys: ['public-transport'],
    type: 'single',
    valueType: 'number',
    options: [100, 500, 1000, 2000],
  },
  {
    key: 'offers',
    enabled: () => true,
    searchStateKey: 'offers',
    searchApiKeys: ['specials'],
    type: 'single',
    valueType: 'string',
    options: [
      { value: 'last_minute', slug: 'LM' },
      { value: 'early_booker', slug: 'EB' },
      { value: 'special_offer', slug: 'SP' },
    ],
  },
  {
    key: 'holidayType',
    enabled: () => true,
    searchStateKey: 'holidayType',
    searchApiKeys: ['types', 'attributes', 'themes'],
    type: 'single',
    valueType: 'string',
    options: [
      'familyfriendly',
      { value: 'holiday_resort', slug: 'holiday-resort' },
      'residence',
      'citytrips',
    ],
  },
  {
    key: 'special',
    enabled: () => true,
    searchStateKey: 'special',
    searchApiKeys: ['attributes', 'themes'],
    type: 'multi',
    valueType: 'array',
    options: [
      { value: 'swiss_peak', slug: 'swisspeak' },
      'casa',
      'workation',
      'sustainable',
    ],
  },
  {
    key: 'pool',
    enabled: () => true,
    searchStateKey: 'pool',
    searchApiKeys: ['attributes'],
    type: 'single',
    valueType: 'string',
    options: [
      { value: 'pool_children', slug: 'pool-children' },
      'pool',
      { value: 'pool_private', slug: 'pool-private' },
      { value: 'pool_indoor', slug: 'pool-indoor' },
    ],
  },
  {
    key: 'fishing',
    enabled: () => true,
    searchStateKey: 'fishing',
    searchApiKeys: ['attributes'],
    type: 'multi',
    valueType: 'array',
    options: [
      'fishing',
      { value: 'fishing_certified', slug: 'fishing-certified' },
    ],
  },
  {
    key: 'activities',
    enabled: () => true,
    searchStateKey: 'fishing',
    searchApiKeys: ['attributes'],
    type: 'multi',
    valueType: 'array',
    options: [
      'skiing',
      'hiking',
      'golfing',
      'cycling',
      'wellness',
      'tennis',
      'surfing',
      'sailing',
      'mountainbiking',
      'riding',
      'crosscountryskiing',
    ],
  },
  {
    key: 'freeweekCategory',
    enabled: () => useFlag('freeweek').value,
    searchStateKey: 'freeweekCategory',
    searchApiKeys: [],
    type: 'multi',
    valueType: 'array',
    options: Object.keys(FREEWEEK_PARAMS),
  },
]

export const FILTER_CONF_MAP = Object.fromEntries(
  FILTER_CONFS.map((conf) => [conf.key, conf]),
)
